import {urlEncode, runWhile, SimpleApi} from '@oc/utils'

const apiGatewayName = 'IbtAPIGateway'

export enum DeployStepStatus {
    succeeded = 'succeeded',
    failed = 'failed',
    running = 'running'
}

export enum DeployStepMessageType {
    error = 'error',
    info = 'info'
}

export enum DeploymentStatus {
    succeeded = 'SUCCEEDED',
    running = 'RUNNING',
    failed = 'FAILED',
    idle = 'IDLE',
    timedout = 'TIMEDOUT',
    aborted = 'ABORTED',
    scheduled = 'SCHEDULED',
    invalid = 'INVALID',
    error = 'ERROR'
}

export const deploymentStatusesByAction = {
    selectable: [
        DeploymentStatus.succeeded,
        DeploymentStatus.failed,
        DeploymentStatus.timedout,
        DeploymentStatus.aborted,
        DeploymentStatus.invalid
    ]
}

export enum DeploymentStatusColor {
    succeeded = '#97C218',
    running = '#00BFFF',
    failed = '#DD101A',
    idle = '#FFDD00',
    invalid = '#79828A',
    unknown = '#FF00CC'
}

export enum LogLevel {
    info = 'INFO',
    debug = 'DEBUG',
    warn = 'WARN',
    error = 'ERROR',
    fatal = 'FATAL'
}

export enum DeployType {
    Validate = 'validate',
    Configs = 'deployConfigs',
    All = 'deployAll',
    ContiloopAi = 'deployContiloopAi',
    Workloads = 'deployWorkloads'
}

export interface DeployStepMessage {
    text: string
    type: DeployStepMessageType
    timestamp: number
    data: string
}

export interface DeployMessage {
    rkUuid?: string
    stepName: string
    status: DeployStepStatus
    messages: DeployStepMessage[]
}

export interface DeployStepsGroups {
    [key: string]: Array<string>
}

export interface StartDeployParameters {
    deleteDasFiles: boolean
    trustOpcUaCertificates: boolean
    uploadDisabled: boolean
    logLevel: LogLevel
    deployType: DeployType
}

export interface ReadyKitSoftwares {
    DASVersion?: string
    DASWebVersion?: string
    InfluxDBVersion?: string
    TelegrafVersion?: string
}

export interface ReadyKitSoftwareVersion {
    account: string
    rkUuid: string
    sw: ReadyKitSoftwares
    version: number
}

export interface StatusInfo {
    started: number
    finished: number
    executionArn: string
    executionName: string
    deployType: DeployType
    parameters: DeployParamProperty
    status: DeploymentStatus
}

export interface DeployParamProperty {
    deleteDasFiles: boolean
    trustOpcUaCertificates: boolean
    uploadDisabled: boolean
    logLevel: string
}

export interface LastSucessStatusInfo {
    deployConfigs?: StatusInfo
    deployAll?: StatusInfo
    validate?: Omit<StatusInfo, 'parameters'>
}

export interface DeployScheduleInfoItem {
    timestamp: number
    deployType: DeployType
    parameters: DeployParamProperty
    error?: boolean
}

export interface DeployScheduleInfo {
    deployConfigs?: DeployScheduleInfoItem
    deployAll?: DeployScheduleInfoItem
    validate?: DeployScheduleInfoItem
}

export interface LastStatusInfo {
    timestamp?: number
    deployType?: DeployType
    status?: DeploymentStatus
}

export interface DeploymentStatusInfo {
    rkUuid: string
    account: string
    version: number
    last: StatusInfo
    lastSuccess: LastSucessStatusInfo
    scheduled?: DeployScheduleInfo
}

export interface ReadyKitMassDeployInfo {
    accountName: string
    s2aAccountId: string
    customerDescription: string
    name: string
    id: string
    sw: ReadyKitSoftwares
    checked: boolean
    lastSucessfulStatus?: LastSucessStatusInfo
    lastStatusChange?: StatusInfo
    scheduleInfo?: DeployScheduleInfo
    versionStatus: number
    versionSw: number
}

export interface MassDeploy {
    account: string
    deployType: DeployType
    rkUuid: string
    executionName: string
    deleteDasFiles: boolean
    trustOpcUaCertificates: boolean
    uploadDisabled: boolean
    logLevel: LogLevel
}

export interface DeploySubscriptions {
    [topic: string]: {
        unsubscribeAction: () => void
    }
}

export class DeployService {
    public async getLastDeploy(customer: string, rk: string): Promise<DeploymentStatusInfo> {
        const path = urlEncode`-deploy/deploy/${customer}/${rk}/status-rk`
        const deploy = (await SimpleApi.get(apiGatewayName, path)) as DeploymentStatusInfo

        return deploy
    }

    public async getDeployStepsGroups(customer: string, rk: string): Promise<DeployStepsGroups> {
        const path = urlEncode`-deploy/deploy/${customer}/${rk}/steps-groups`
        return await SimpleApi.get(apiGatewayName, path)
    }

    public async beginDeploy(customer: string, rk: string, deployParams: StartDeployParameters): Promise<string> {
        const path = urlEncode`-deploy/deploy/${customer}/${rk}/start`
        const customExecutionName = `${rk}_${Date.now()}`

        await SimpleApi.post(apiGatewayName, path, {
            body: {
                ...deployParams,
                executionName: customExecutionName
            }
        })

        return customExecutionName
    }

    public async getLastDeploySteps(customer: string, rk: string): Promise<DeployMessage[]> {
        const path = urlEncode`-deploy/deploy/${customer}/${rk}/start`
        const response = await SimpleApi.get(apiGatewayName, path)

        if ('history' in response && 'steps' in response['history']) {
            return response['history']['steps']
        }

        return []
    }

    public async beginMassDeploy(data: MassDeploy[]): Promise<void> {
        const path = urlEncode`-deploy/start-all-rks`

        await SimpleApi.post(apiGatewayName, path, {
            body: {
                rks: data
            }
        })
    }

    public async cancelDeploy(customer: string, rk: string, deploymentType: DeployType): Promise<void> {
        const path = urlEncode`-deploy/deploy/${customer}/${rk}/cancel`

        await SimpleApi.post(apiGatewayName, path, {
            body: {
                deployType: deploymentType
            }
        })
    }

    public async beginValidate(customer: string, rk: string): Promise<string> {
        const path = urlEncode`-deploy/deploy/${customer}/${rk}/validate`
        const customExecutionName = `${rk}_${Date.now()}`

        await SimpleApi.post(apiGatewayName, path, {
            body: {
                executionName: customExecutionName,
                deployType: DeployType.Validate
            }
        })

        return customExecutionName
    }

    public async getRkSwVersion(): Promise<ReadyKitSoftwareVersion[]> {
        const path = urlEncode`/rk-sw-versions`
        const response = (await SimpleApi.get(apiGatewayName, path)).items

        return response
    }

    public async getAllDeployStatuses(): Promise<DeploymentStatusInfo[]> {
        const allDeployStatusInfos: DeploymentStatusInfo[] = []
        const path = urlEncode`-deploy/status-all`
        const parameters = new URLSearchParams({})

        await runWhile(async () => {
            const lastEvaluatedKey = parameters.get('lastEvaluatedKey')
            const response = await SimpleApi.get(
                apiGatewayName,
                path,
                lastEvaluatedKey
                    ? {
                          queryParams: {
                              lastEvaluatedKey: lastEvaluatedKey
                          }
                      }
                    : undefined
            )

            if (!response || !response.items) {
                return false
            }

            for (const item of response.items) {
                allDeployStatusInfos.push(item)
            }

            if (response.lastEvaluatedKey) parameters.set('lastEvaluatedKey', response.lastEvaluatedKey)

            return !!response.lastEvaluatedKey
        }, 0)

        return allDeployStatusInfos
    }
}
